import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@babackapp/customer-ui/components/select";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { normalizeLanguageCode } from "@babackapp/translations/language-utils";
import { GlobeIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { persistWidgetLanguage } from "../hooks/use-widget-language";

function getDisplayName(localeCode: string): string {
  try {
    const names = new Intl.DisplayNames([localeCode, "en"], { type: "language" });
    const langCode = localeCode.split("-")[0];
    const name = names.of(langCode);
    if (name) return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    // fall through
  }
  return localeCode;
}

export function LanguageSelector() {
  const { shopId } = useParams<{ shopId: string }>();
  const { data: shop } = usePublicShop(shopId!);
  const { i18n } = useTranslation("return-widget-v3");

  const languages = shop?.preferences?.languages ?? [];

  if (languages.length <= 1) return null;

  const currentLangCode = i18n.language ?? "en";
  const currentLocale =
    languages.find((l) => normalizeLanguageCode(l) === currentLangCode) ??
    languages[0];

  const handleChange = (localeCode: string) => {
    const code = normalizeLanguageCode(localeCode);
    persistWidgetLanguage(code);
    i18n.changeLanguage(code);
  };

  return (
    <div className="flex items-center gap-1.5 text-muted-foreground">
      <GlobeIcon className="size-3.5 shrink-0" />
      <Select value={currentLocale} onValueChange={handleChange}>
        {/* The trigger sits in the widget chrome next to the close button, so it
            shows the base code only; the list keeps the language names. A regional
            code like zh-cn would widen the chrome past what the stepper bar
            reserves for it (`pr-24` in line-item/layout.tsx). */}
        <SelectTrigger className="border-none shadow-none h-auto py-0 px-0 text-xs focus:ring-0 w-auto gap-1">
          {currentLangCode.split("-")[0].toUpperCase()}
        </SelectTrigger>
        {/* "popper" anchors via Floating UI to the trigger's own rect. The
            default "item-aligned" mode computes its position from the trigger's
            offset parent instead, which breaks once the trigger sits inside the
            widget chrome's `absolute top-6 right-6` box: in production it landed
            the dropdown 1055px down, past the bottom of the viewport. */}
        <SelectContent position="popper">
          {languages.map((locale) => (
            <SelectItem key={locale} value={locale} className="text-xs">
              {getDisplayName(locale)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
